/**
 * RequireAuth — 로그인 필수 라우트 보호 컴포넌트.
 *
 * 정책 (AdminGuard 와 동일, role 검사만 제거):
 *  1. 세션 복원 중(`isLoading`) → 최소 안내 (비인증 flash 방지).
 *  2. 미인증 → `/` 로 replace navigate + 로그인 모달 오픈. 현 path 를
 *     ``returnTo`` 로 전달 → 로그인 성공 후 LoginModal 이 해당 path 로 복귀.
 *  3. 인증됨 → children(또는 <Outlet/>) 렌더.
 *
 * 레이아웃 라우트로도(`<Route element={<RequireAuth/>}>`), 단일 래퍼로도
 * (`<RequireAuth><Page/></RequireAuth>`) 사용 가능.
 *
 * 메뉴 진입 차단용 1차 UX 필터. 실제 보안 경계는 BE 인증(401)이 담당하며,
 * 공개 엔드포인트(leaderboard 등)는 본 가드가 FE 단에서만 추가 차단한다.
 */

import { useAuthModalStore } from '@/stores/authModalStore'
import { useAuthStore } from '@/stores/authStore'
import { useEffect } from 'react'
import { Outlet, useLocation } from 'react-router'

interface RequireAuthProps {
  children?: React.ReactNode
}

export function RequireAuth({ children }: RequireAuthProps) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const isLoading = useAuthStore((s) => s.isLoading)
  const user = useAuthStore((s) => s.user)
  const openAuthModal = useAuthModalStore((s) => s.open)
  const location = useLocation()

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      const returnTo = `${location.pathname}${location.search}${location.hash}`
      openAuthModal('signin', returnTo)
    }
  }, [isLoading, isAuthenticated, openAuthModal, location.pathname, location.search, location.hash])

  if (isLoading) {
    return (
      <div className="flex min-h-[240px] items-center justify-center text-[12px] text-muted-foreground">
        세션 확인 중…
      </div>
    )
  }

  if (!isAuthenticated) {
    // 비로그인: 홈으로 보내지 않고 현재 주소에 머문다 (QA H-7).
    // 리다이렉트하면 주소창의 공유 링크가 사라져, 로그인 후 돌아올 곳을
    // 사용자도 우리도 알 수 없게 된다. 모달은 위 effect 가 띄운다.
    return (
      <div className="flex min-h-[240px] items-center justify-center text-[12px] text-muted-foreground">
        로그인이 필요합니다. 로그인하면 이 화면으로 돌아옵니다.
      </div>
    )
  }

  if (user?.registrationStatus && user.registrationStatus !== 'approved') {
    return (
      <div className="mx-auto my-12 max-w-xl rounded-md border border-amber-300 bg-amber-50 p-6 text-center text-amber-950">
        <h1 className="mb-2 text-lg font-semibold">
          {user?.registrationStatus === 'rejected'
            ? '가입 신청이 승인되지 않았습니다'
            : '가입 승인 대기 중입니다'}
        </h1>
        <p className="m-0 text-sm leading-6">
          {user?.registrationStatus === 'rejected'
            ? user.registrationReviewReason || '자세한 내용은 플랫폼 관리자에게 문의해 주세요.'
            : '관리자가 가입을 승인하면 데이터셋·모델·큐레이션·리더보드 기능을 사용할 수 있습니다.'}
        </p>
      </div>
    )
  }

  return children ? <>{children}</> : <Outlet />
}
